import React, { useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhoneVolume, 
  faFileContract, 
  faHandHoldingDollar, 
  faFile, 
  faHandshake, 
  faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';

import CallToAction from '../components/sections/CallToAction';
import ContactForm from '../components/sections/ContactForm';

const PageBanner = styled.section`
  background: linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.7)), url('/images/alexis-antoine-Xal6kYdK7rM-unsplash.jpg');
  background-size: cover;
  background-position: center;
  padding: 120px 0 80px;
  text-align: center;
  color: #fff;
  
  h1 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 20px;
    
    span {
      color: #ff9800;
    }
    
    @media (max-width: 768px) {
      font-size: 2.3rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    max-width: 700px;
    margin: 0 auto 30px;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const ProcessSection = styled.section`
  padding: 80px 0;
  background-color: #fff;
  
  .section-title {
    text-align: center;
    margin-bottom: 50px;
    
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
      
      span {
        color: #2e7d32;
      }
    }
    
    p {
      color: #666;
      font-size: 1.1rem;
      max-width: 700px;
      margin: 0 auto;
    }
  }
  
  .process-container {
    display: flex;
    flex-wrap: wrap;
  }
  
  .process-step {
    width: 100%;
    margin-bottom: 30px;
    
    @media (min-width: 768px) {
      width: 50%;
      padding: 0 15px;
    }
    
    @media (min-width: 992px) {
      width: 33.333%;
    }
    
    .step-card {
      height: 100%;
      border: none;
      border-radius: 15px;
      padding: 25px;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
      }
      
      .step-number {
        position: absolute;
        top: 15px;
        right: 15px;
        width: 40px;
        height: 40px;
        background-color: #2e7d32;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 1.2rem;
        color: #fff;
        z-index: 2;
      }
      
      .step-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background-color: rgba(46, 125, 50, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        color: #2e7d32;
        margin-bottom: 15px;
      }
      
      .step-title {
        font-size: 1.3rem;
        font-weight: 700;
        margin-bottom: 10px;
        padding-right: 45px; /* Make room for the number */
        color: #333;
      }
      
      .step-description {
        color: #666;
        font-size: 0.95rem;
        line-height: 1.5;
        max-height: 250px;
        overflow-y: auto;
        
        p {
          margin-bottom: 10px;
        }
        
        ul {
          padding-left: 20px;
          
          li {
            margin-bottom: 6px;
          }
        }
      }
    }
  }
`;

const FAQSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  .section-title {
    text-align: center;
    margin-bottom: 50px;
    
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
      
      span {
        color: #2e7d32;
      }
    }
    
    p {
      color: #666;
      font-size: 1.1rem;
      max-width: 700px;
      margin: 0 auto;
    }
  }
  
  .faq-container {
    max-width: 900px;
    margin: 0 auto;
  }
  
  .faq-card {
    border: none;
    border-radius: 15px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    
    .card-header {
      background-color: #fff;
      padding: 20px 25px;
      border-radius: 15px !important;
      border-bottom: none;
      cursor: pointer;
      
      h3 {
        font-size: 1.2rem;
        font-weight: 600;
        color: #333;
        margin: 0;
        transition: color 0.3s ease;
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        .icon {
          font-size: 1.4rem;
          color: #2e7d32;
          font-weight: 300;
        }
        
        &:hover {
          color: #2e7d32;
        }
      }
    }
    
    .card-body {
      padding: 0 25px 20px;
      color: #666;
      font-size: 1rem;
      line-height: 1.6;
      display: none;
      
      &.active {
        display: block;
      }
    }
  }
`;

const HowItWorksPage = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  
  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  const processSteps = [
    {
      number: 1,
      icon: faPhoneVolume,
      title: "Contact Us",
      description: (
        <div>
          <p>
            Start by reaching out to us. You can either:
          </p>
          <ul>
            <li>Fill out our simple online form with basic details about your property</li>
            <li>Call us directly at (786) 460-6046</li>
            <li>Send an email to connect@beringsolutions.com</li>
          </ul>
          <p>
            When contacting us, provide as much information as you can about your land: location, size, zoning, 
            any structures or utilities, access roads, and why you're considering selling.
          </p>
        </div>
      )
    },
    {
      number: 2,
      icon: faFileContract,
      title: "Property Research & Evaluation",
      description: (
        <div>
          <p>
            After we receive your information, our team will begin researching your property to determine its value. 
            We consider many factors, including:
          </p>
          <ul>
            <li>Location and accessibility</li>
            <li>Property size and topography</li>
            <li>Zoning and potential use</li>
            <li>Current market conditions in your area</li>
            <li>Comparable sales nearby</li>
          </ul>
          <p>
            Our research is thorough but quick - typically completed within 24-48 hours.
          </p>
        </div>
      )
    },
    {
      number: 3,
      icon: faMoneyBillWave,
      title: "Receive Your Cash Offer",
      description: (
        <div>
          <p>
            Once our evaluation is complete, we'll present you with a fair cash offer for your land. 
            Our offers are:
          </p>
          <ul>
            <li>Clear and straightforward - no hidden fees or commissions</li>
            <li>No-obligation - you're free to accept, reject, or counter</li>
            <li>Valid for a reasonable time period to give you space to decide</li>
          </ul>
          <p>
            We're happy to explain how we arrived at our offer amount and answer any questions you may have.
          </p>
        </div>
      )
    },
    {
      number: 4,
      icon: faFile,
      title: "Accept & Paperwork",
      description: (
        <div>
          <p>
            If you accept our offer, we'll prepare a simple purchase agreement outlining the terms 
            of the sale. Our contracts are straightforward and free of complex legal jargon.
          </p>
          <p>
            You can review the paperwork at your own pace, and we encourage you to have your attorney 
            review it if you wish. We're available to answer any questions and explain any terms.
          </p>
          <p>
            Once you're comfortable, simply sign the agreement and send it back to us.
          </p>
        </div>
      )
    },
    {
      number: 5,
      icon: faHandshake,
      title: "Closing Process",
      description: (
        <div>
          <p>
            We work with professional title companies and closing attorneys to ensure a smooth 
            and legally compliant transaction. During this phase:
          </p>
          <ul>
            <li>A title search is conducted to ensure clear ownership</li>
            <li>Closing documents are prepared</li>
            <li>We coordinate with you to set a closing date that works for your schedule</li>
          </ul>
          <p>
            Unlike traditional real estate transactions, our closings can happen quickly - as soon as 
            7-14 days if needed, or longer if you prefer.
          </p>
        </div>
      )
    },
    {
      number: 6,
      icon: faHandHoldingDollar,
      title: "Get Paid",
      description: (
        <div>
          <p>
            On the agreed closing date, the final paperwork is signed, and you receive payment 
            for your land. We offer several payment options:
          </p>
          <ul>
            <li>Wire transfer directly to your bank account</li>
            <li>Cashier's check</li>
            <li>Other payment methods as needed</li>
          </ul>
          <p>
            With the deed transferred and payment complete, you can move forward with no more 
            property tax bills, maintenance costs, or other obligations related to the land.
          </p>
        </div>
      )
    }
  ];

  const faqs = [
    {
      question: "How quickly can I get an offer for my land?",
      answer: "We typically provide cash offers within 24-48 hours after receiving your property information and completing our research."
    },
    {
      question: "Do I need to clean up or improve my land before selling it to you?",
      answer: "No. We buy land in as-is condition. There's no need to clean it up, remove structures, or make any improvements whatsoever. We handle all of that after purchase."
    },
    {
      question: "What types of land do you buy?",
      answer: "We purchase all types of land including vacant lots, rural acreage, recreational property, farm and ranch land, development land, commercial parcels, and more. We buy land in any condition and location."
    },
    {
      question: "Are there any fees or commissions when selling to you?",
      answer: "Absolutely not. When you sell directly to us, there are no real estate agent commissions, no listing fees, and no closing costs. The offer we make is the amount you receive."
    },
    {
      question: "How long does the closing process take?",
      answer: "It depends on your unique situation and may take from a few weeks to a few months. However, we can also accommodate your timeline if you need more time. You choose the closing date that works best for you."
    },
    {
      question: "What if I owe back taxes on my property?",
      answer: "We can still buy your property even if you owe back taxes. In most cases, the tax amount can be paid from the proceeds at closing. We'll work with you to find a solution."
    }
  ];

  return (
    <>
      <PageBanner>
        <Container>
          <h1>How It <span>Works</span></h1>
          <p>
            Our simple 6-step process makes selling your land fast, easy, and stress-free.
            Here's exactly what to expect when you work with us.
          </p>
        </Container>
      </PageBanner>
      
      <ProcessSection>
        <Container>
          <div className="section-title">
            <h2>Our Simple <span>Process</span></h2>
            <p>
              We've streamlined the land-selling process to make it as easy as possible for you.
              Here's how we buy your land in six straightforward steps:
            </p>
          </div>
          
          <div className="process-container">
            {processSteps.map((step) => (
              <div key={step.number} className="process-step">
                <Card className="step-card">
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon">
                    <FontAwesomeIcon icon={step.icon} />
                  </div>
                  <h3 className="step-title">{step.title}</h3>
                  <div className="step-description">
                    {step.description}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </Container>
      </ProcessSection>
      
      <FAQSection>
        <Container>
          <div className="section-title">
            <h2>Frequently Asked <span>Questions</span></h2>
            <p>
              Get answers to common questions about selling your land to us.
            </p>
          </div>
          
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <Card key={index} className="faq-card">
                <Card.Header onClick={() => toggleFaq(index)}>
                  <h3>
                    {faq.question}
                    <span className="icon">{activeIndex === index ? '−' : '+'}</span>
                  </h3>
                </Card.Header>
                <Card.Body className={activeIndex === index ? 'active' : ''}>
                  <p>{faq.answer}</p>
                </Card.Body>
              </Card>
            ))}
          </div>
        </Container>
      </FAQSection>
      
      <CallToAction 
        title="Ready to Get Started?"
        subtitle="Take the first step toward selling your land. Contact us today for your no-obligation cash offer."
        primaryButtonText="Get Your Cash Offer"
        secondaryButtonText="Call Us Now"
        primaryButtonLink="/get-offer"
        secondaryButtonLink="tel:7864606046"
      />
      
      <ContactForm />
    </>
  );
};

export default HowItWorksPage;
